import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import AudioPlayer from '../components/AudioPlayer';
import { useTheme } from './ThemeContext';
import { projectsService } from '../services/projects';

interface GlobalAudioPlayerState {
  audioBlob: Blob | null;
  projectId: number | null;
  projectName: string | null;
  voiceId: string | null;
  textContent: string | null;
}

interface GlobalAudioPlayerContextType {
  setAudioPlayer: (state: GlobalAudioPlayerState) => void;
  clearAudioPlayer: () => void;
  audioRef: React.RefObject<HTMLAudioElement>;
}

const GlobalAudioPlayerContext = createContext<GlobalAudioPlayerContextType | undefined>(undefined);

export function GlobalAudioPlayerProvider({ children }: { children: React.ReactNode }) {
  const { theme } = useTheme();
  const [audioState, setAudioState] = useState<GlobalAudioPlayerState>(() => {
    // Load from localStorage on mount
    const saved = localStorage.getItem('globalAudioPlayer');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          audioBlob: null, // Blob can't be stored in localStorage, will be reloaded
          projectId: parsed.projectId,
          projectName: parsed.projectName,
          voiceId: parsed.voiceId,
          textContent: parsed.textContent,
        };
      } catch (e) {
        console.error('Failed to parse saved audio player state:', e);
      }
    }
    return {
      audioBlob: null,
      projectId: null,
      projectName: null,
      voiceId: null,
      textContent: null,
    };
  });
  const audioRef = useRef<HTMLAudioElement>(null);
  const [loadingAudio, setLoadingAudio] = useState(false);

  const clearAudioPlayer = () => {
    setAudioState({
      audioBlob: null,
      projectId: null,
      projectName: null,
      voiceId: null,
      textContent: null,
    });
    localStorage.removeItem('globalAudioPlayer');
  };

  // Load audio blob from API when state is restored from localStorage
  useEffect(() => {
    if (audioState.projectId && !audioState.audioBlob && !loadingAudio) {
      setLoadingAudio(true);
      projectsService.readScript(audioState.projectId)
        .then((blob) => {
          setAudioState(prev => ({ ...prev, audioBlob: blob }));
          setLoadingAudio(false);
        })
        .catch((err: any) => {
          console.error('Failed to reload audio from localStorage:', err);
          setLoadingAudio(false);
          // Clear state if reload fails
          clearAudioPlayer();
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioState.projectId, audioState.audioBlob, loadingAudio]);

  const setAudioPlayer = (state: GlobalAudioPlayerState) => {
    setAudioState(state);
    // Save to localStorage (without blob)
    if (state.projectId && state.voiceId) {
      localStorage.setItem('globalAudioPlayer', JSON.stringify({
        projectId: state.projectId,
        projectName: state.projectName,
        voiceId: state.voiceId,
        textContent: state.textContent,
      }));
    }
  };

  return (
    <GlobalAudioPlayerContext.Provider value={{ setAudioPlayer, clearAudioPlayer, audioRef }}>
      {children}
      {/* Global Mini Player - Always visible when audio is loaded */}
      {audioState.audioBlob && audioState.projectId && (
        <div className={`fixed bottom-0 left-0 right-0 z-[90] w-full ${theme === 'dark' ? 'dark' : ''}`}>
          <div className={`bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 shadow-lg w-full`}>
            <AudioPlayer
              projectId={audioState.projectId}
              projectName={audioState.projectName || undefined}
              audioBlob={audioState.audioBlob}
              voiceId={audioState.voiceId || undefined}
              textContent={audioState.textContent || undefined}
              showText={false}
              audioRef={audioRef}
              onClose={clearAudioPlayer}
            />
          </div>
        </div>
      )}
    </GlobalAudioPlayerContext.Provider>
  );
}

export function useGlobalAudioPlayer() {
  const context = useContext(GlobalAudioPlayerContext);
  if (!context) {
    throw new Error('useGlobalAudioPlayer must be used within GlobalAudioPlayerProvider');
  }
  return context;
}

